// Requests: create one, follow it, approve a quote, sign off the work.
import { api } from "../lib/api";
import { SERVICE_CATEGORIES } from "../lib/catalog";
import { fmt, t } from "../lib/i18n";
import { navigate } from "../lib/router";
import { session } from "../lib/session";
import { h, invalidate, local, resource } from "../lib/ui";
import { act, Async, Button, Field, formData, isBusy, Select, TicketStatus, toast } from "../shared/components";
import { Icon } from "../shared/icons";

export function NewRequest(props: { homeId: string }) {
  const dash = resource(`my.dash.${props.homeId}`, () => api.get(`/api/my/homes/${props.homeId}/dashboard`));
  const s = local("screen:request", () => ({ priority: "P3" }));
  const needsApproval = dash.data?.me?.perms?.requestNeedsApproval;
  const submit = (e: Event) => {
    const d = formData(e);
    act("request.create", async () => {
      const res = await api.post(`/api/my/homes/${props.homeId}/tickets`, { service: d.service, title: d.title, description: d.description, priority: s.priority });
      invalidate(`my.dash.${props.homeId}`);
      toast(res.status === "awaiting_resident_approval" ? t("customer.request.sentForApproval") : t("customer.request.sent"));
      navigate(`/requests/${res.id}`, { replace: true });
    });
  };
  const urgencies = ["P1", "P2", "P3", "P4"];
  return (
    <form class="stack loose" onSubmit={submit}>
      <div class="stack tight">
        <h1>{t("customer.request.heading")}</h1>
        <p class="muted">{t("customer.request.intro")}</p>
      </div>
      <Field label={t("customer.request.what")} id="rq-title"><input class="input" id="rq-title" name="title" required maxLength={160} placeholder={t("customer.request.whatPlaceholder")} /></Field>
      <Field label={t("customer.request.kind")} id="rq-svc"><Select id="rq-svc" name="service" value="plumbing" options={SERVICE_CATEGORIES.map((x) => ({ value: x, label: t(`service.${x}`) }))} /></Field>
      <fieldset class="stack tight" style="border:0;padding:0;margin:0">
        <legend class="label" style="margin-bottom:8px">{t("customer.request.urgency")}</legend>
        {urgencies.map((p) => (
          <label class={`check panel ${s.priority === p ? "" : ""}`} style={`padding:12px 14px;border-color:${s.priority === p ? "var(--c-primary)" : "var(--c-line)"}`}>
            <input type="radio" name="priority" value={p} checked={s.priority === p} onChange={() => s.set({ priority: p })} />
            <span class="stack tight" style="gap:0">
              <span class="strong">{t(`customer.request.urg.${p}.label`)}</span>
              <span class="small muted">{t(`customer.request.urg.${p}.help`)}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {s.priority === "P1" ? <div class="note danger small">{t("customer.request.emergencyNote")}</div> : null}
      <Field label={t("customer.request.details")} id="rq-desc" hint={t("customer.request.detailsHint")}><textarea class="textarea" id="rq-desc" name="description" maxLength={4000} /></Field>
      {needsApproval && s.priority !== "P1" ? <div class="note small">{t("customer.request.remoteNote")}</div> : null}
      <Button type="submit" size="large" block busy={isBusy("request.create")}>{t("customer.request.send")}</Button>
    </form>
  );
}

export function TicketView(props: { id: string }) {
  const r = resource(`my.ticket.${props.id}`, () => api.get(`/api/my/tickets/${props.id}`));
  const s = local(`screen:ticket.${props.id}`, () => ({ rating: 0 }));
  const reload = () => { r.reload(); invalidate("my.dash."); };
  return Async(r, (d: any) => {
    const tk = d.ticket;
    const est = d.estimates.find((e: any) => e.status === "pending");
    const a = d.approval;
    const approve = (e: Event) => {
      const f = formData(e);
      act("est.approve", async () => {
        await api.post(`/api/my/tickets/${tk.id}/estimates/${a.estimateId}/approve`, { writtenName: f.writtenName ?? "" });
        reload();
      }, t("customer.ticket.approved"));
    };
    const reject = (secondQuote: boolean) => act(`est.reject.${secondQuote}`, async () => {
      await api.post(`/api/my/tickets/${tk.id}/estimates/${a.estimateId}/reject`, { secondQuote });
      reload();
    }, secondQuote ? t("customer.ticket.secondQuoteAsked") : t("customer.ticket.declined"));
    const approveRequest = () => act("req.approve", async () => { await api.post(`/api/my/tickets/${tk.id}/approve-request`); reload(); }, t("customer.ticket.requestApproved"));
    const signoff = (accept: boolean) => act(`signoff.${accept}`, async () => {
      await api.post(`/api/my/tickets/${tk.id}/signoff`, { accept, rating: accept && s.rating ? s.rating : null });
      reload();
    }, accept ? t("customer.ticket.signedOff") : t("customer.ticket.reported"));

    return (
      <div class="stack loose">
        <div class="stack tight">
          <div class="row"><TicketStatus status={tk.status} /><span class="small muted">#{tk.number} · {t(`service.${tk.service}`)}</span></div>
          <h1>{tk.title}</h1>
          {tk.description ? <p class="muted" style="white-space:pre-wrap">{tk.description}</p> : null}
        </div>

        {est && a ? (
          <section class="decision">
            <div class="row between top">
              <div class="stack tight grow" style="gap:2px">
                <span class="strong">{t("customer.ticket.quote")}</span>
                <span class="small" style="white-space:pre-wrap">{est.description}</span>
              </div>
              <span class="amount">{fmt.money(est.amount_cents)}</span>
            </div>
            {a.canApprove ? (
              <form class="stack" onSubmit={approve}>
                <span class="small muted">{t(`customer.ticket.tier.${a.tier}`)}</span>
                {a.tier !== "one_click" ? (
                  <Field label={t("customer.ticket.typeName", { name: session.me?.name ?? "" })} id="wn">
                    <input class="input" id="wn" name="writtenName" autocomplete="name" required />
                  </Field>
                ) : null}
                <div class="row">
                  <Button type="submit" busy={isBusy("est.approve")}>{t("customer.ticket.approve", { amount: fmt.money(est.amount_cents) })}</Button>
                  {a.tier === "quote" ? <Button kind="secondary" busy={isBusy("est.reject.true")} onClick={() => reject(true)}>{t("customer.ticket.secondQuote")}</Button> : null}
                  <Button kind="ghost" busy={isBusy("est.reject.false")} onClick={() => reject(false)}>{t("customer.ticket.decline")}</Button>
                </div>
              </form>
            ) : <div class="note small">{t("customer.ticket.overLimit")}</div>}
          </section>
        ) : null}

        {d.canApproveRequest ? (
          <section class="decision">
            <span class="strong">{t("customer.ticket.remoteRequest")}</span>
            <div class="row"><Button busy={isBusy("req.approve")} onClick={approveRequest}>{t("customer.ticket.approveRequest")}</Button></div>
          </section>
        ) : null}

        {d.canSignOff ? (
          <section class="decision">
            <span class="strong">{t("customer.ticket.signoffQuestion")}</span>
            <div class="row" role="radiogroup" aria-label={t("customer.ticket.rating")}>
              {[1, 2, 3, 4, 5].map((n) => (
                <button type="button" class="icon-btn" role="radio" aria-checked={String(s.rating === n)} aria-label={t("customer.ticket.stars", { count: n })}
                  style={`color:${n <= s.rating ? "var(--c-accent)" : "var(--c-line-strong)"};font-size:24px`} onClick={() => s.set({ rating: n })}>★</button>
              ))}
            </div>
            <div class="row">
              <Button busy={isBusy("signoff.true")} onClick={() => signoff(true)}>{t("customer.ticket.accept")}</Button>
              <Button kind="secondary" busy={isBusy("signoff.false")} onClick={() => signoff(false)}>{t("customer.ticket.problem")}</Button>
            </div>
            <span class="xs muted">{t("customer.ticket.autoSignoff")}</span>
          </section>
        ) : null}

        <section>
          <div class="section-title"><h2>{t("customer.ticket.progress")}</h2></div>
          <div class="list" style="padding:16px">
            <div class="timeline">
              {d.events.map((e: any) => (
                <div class="ev">
                  <div class="stack tight" style="gap:0">
                    <span class="small strong">{e.kind === "created" ? t("customer.ticket.evCreated") : e.kind === "approval" ? t("customer.ticket.evApproved") : t(`ticketStatus.${e.to_status}`)}</span>
                    <span class="xs muted">{fmt.dateTime(e.at)}{e.actor ? ` · ${e.actor}` : ""}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {d.estimates.filter((e: any) => e.status !== "pending").length ? (
          <section>
            <div class="section-title"><h2>{t("customer.ticket.costs")}</h2></div>
            <div class="list">
              {d.estimates.filter((e: any) => e.status !== "pending").map((e: any) => (
                <div class="list-row">
                  <Icon name={e.status === "approved" ? "check" : "x"} />
                  <span class="grow small">{e.description}</span>
                  <span class="tabular strong">{fmt.money(e.amount_cents)}</span>
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    );
  }, 5);
}
